/**
 * Engine calculations region for Engine Editor — transformation × calculation
 * cell per scope-doc §2 cell-by-cell routing (sub-shape 1a code-PR /
 * impl-home=code).
 *
 * Cluster-header + internal-sections shape inherits Atlas §6.5 verbatim
 * per scope-doc §3.2 copy principle (read-altitude content shared between
 * surfaces; surface differs only in affordance overlay).
 *
 * Engine Editor renders sub-shape 1a negative affordance:
 *   "Code-level. Edits via deployment + git history."
 * per scope-doc §4.2 line 469 verbatim. No edit affordance added.
 *
 * No substrate fetch — engine RPCs are code-altitude not data-altitude.
 */

import type { JSX } from 'react';
import { Calculator } from 'lucide-react';

export default function EngineEditorEngineCalculationsRegion(): JSX.Element {
  return (
    <section
      id="engine-calculations"
      data-cell-coordinate="transformation-calculation"
      data-region-type="region"
      className="border border-border bg-surface-1 rounded-lg p-4 space-y-3"
    >
      <div className="flex items-start gap-2">
        <Calculator size={16} className="text-ink-secondary flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <h2 className="text-sm font-semibold text-ink-bright">Engine calculations</h2>
          <p className="text-xs text-ink-secondary mt-1">The math the engine performs.</p>
        </div>
      </div>

      {/* COMPOSABILITY: per-section "Last changed X days ago" indicator slot — wires post-Day 92 */}

      {/* 2 internal sections per scope §6.5 verbatim */}
      <div className="space-y-2 pl-6">
        <div>
          <h3 className="text-xs font-semibold text-ink-bright">Primary calculations</h3>
          <p className="text-xs text-ink-secondary mt-0.5">
            Present-state profitability across single-quantity and tiered modes + breakdown
            inspection (calculate_profitability + calculate_profitability_tiers +
            calculate_breakdown_extras).
          </p>
        </div>
        <div>
          <h3 className="text-xs font-semibold text-ink-bright">Forward-looking projections</h3>
          <p className="text-xs text-ink-secondary mt-0.5">
            Future-state restock + PO impact projections (forecast_restock +
            calculate_po_impact).
          </p>
        </div>
      </div>

      <p className="text-xs text-ink-secondary pl-6">
        <span className="font-semibold text-ink-bright">Together:</span> the engine&apos;s
        present-state and forward-looking outputs.
      </p>

      {/* Sub-shape 1a negative-affordance copy per scope-doc §4.2 verbatim */}
      <p className="text-xs text-ink-muted italic pl-6 pt-2 border-t border-border-subtle">
        Code-level. Edits via deployment + git history.
      </p>
    </section>
  );
}
